<template>
  <!-- LOADING -->
  <div v-if="!ready" class="container d-flex justify-content-center align-items-center py-5">
    <div class="text-center">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="mb-0 text-muted">Cargando proyección de cartera...</p>
    </div>
  </div>

  <!-- CONTENIDO PRINCIPAL -->
  <div v-else class="proyeccion-container container ">
    <!-- ENCABEZADO + FILTROS -->
    <div class="d-flex justify-content-between align-items-end mb-4 gap-3 flex-wrap">
      <div>
        <h2 class="title mb-1">Proyección de Cartera</h2>
        <p class="subtitle mb-0">
          Resumen de mínimos a pagar y montos para cancelación por estado del crédito.
        </p>
      </div>

      <div class="d-flex align-items-end gap-3 flex-wrap">
        <div>
          <label class="form-label mb-1">Fecha de proyección</label>
          <input
            type="date"
            class="form-control"
            v-model="selectedDate"
            :min="today"
          />
          <small v-if="dateError" class="text-danger">{{ dateError }}</small>
        </div>

        <button class="btn btn-primary" @click="handleConsultar">
          Actualizar
        </button>

        <button class="btn btn-outline-success" @click="handleImprimir" :disabled="!tieneDatos">
          Exportar a Excel
          <i class="fa-solid fa-download ms-2"></i>
        </button>
      </div>
    </div>

    <!-- TARJETAS DE ESTADO -->
    <div class="row g-3">
      <div
        class="col-12 col-md-6 col-xl-3"
        v-for="card in cards"
        :key="card.key"
      >
        <div class="estado-card" :class="card.cardClass">
          <div class="estado-card-header d-flex justify-content-between align-items-center">
            <span class="estado-name">{{ card.title }}</span>
            <span class="badge" :class="card.badgeClass">{{ card.badgeLabel }}</span>
          </div>

          <div class="estado-card-body">
            <div class="estado-monto-principal">
              {{ formatoNumero2(card.data.totalMinimoAPagar) }}
              <span class="moneda">L</span>
            </div>
            <div class="estado-label">Total mínimo a pagar</div>

            <div class="estado-secondary mt-2">
              <span>Para cancelar créditos:</span>
              <strong>{{ formatoNumero2(card.data.totalMinimoCancelarCredito) }} L</strong>
            </div>

            <div class="estado-footer mt-3 d-flex justify-content-between align-items-center">
              <span class="estado-creditos">
                {{ card.data.cantidadCreditos }} créditos
              </span>
              <div class="estado-bar">
                <div
                  class="estado-bar-fill"
                  :style="{ width: getPorcentajeCreditos(card.data.cantidadCreditos) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RESUMEN TOTAL -->
      <div class="col-12 mt-2">
        <div class="total-card">
          <div class="d-flex justify-content-between flex-wrap align-items-center">
            <div>
              <h5 class="mb-1">Total cartera proyectada</h5>
              <p class="mb-0 small text-muted">
                {{ total.cantidadCreditos }} créditos ·
                Mínimo a pagar: {{ formatoNumero2(total.totalMinimoAPagar) }} L ·
                Para cancelar: {{ formatoNumero2(total.totalMinimoCancelarCredito) }} L
              </p>
            </div>
            <div class="text-end">
              <span class="badge rounded-pill bg-light text-dark">
                Fecha de proyección:
                {{ fechaProyeccionFormateada }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Swal from "sweetalert2";
import Security from "../security";
import ExcelJS from "exceljs";

export default {
  name: "ProyeccionCartera",
  data() {
    const hoy = new Date().toISOString().split("T")[0];

    return {
      ready: false,
      moneda: "L",
      proyeccion: {
        activos: {
          totalMinimoAPagar: 0,
          totalMinimoCancelarCredito: 0,
          cantidadCreditos: 0,
        },
        vencidos: {
          totalMinimoAPagar: 0,
          totalMinimoCancelarCredito: 0,
          cantidadCreditos: 0,
        },
        legal: {
          totalMinimoAPagar: 0,
          totalMinimoCancelarCredito: 0,
          cantidadCreditos: 0,
        },
        total: {
          totalMinimoAPagar: 0,
          totalMinimoCancelarCredito: 0,
          cantidadCreditos: 0,
        },
        fechaProyeccion: null,
      },
      today: hoy,
      selectedDate: hoy,
      dateError: "",
    };
  },
  computed: {
    activos() {
      return this.proyeccion.activos || {
        totalMinimoAPagar: 0,
        totalMinimoCancelarCredito: 0,
        cantidadCreditos: 0,
      };
    },
    vencidos() {
      return this.proyeccion.vencidos || {
        totalMinimoAPagar: 0,
        totalMinimoCancelarCredito: 0,
        cantidadCreditos: 0,
      };
    },
    legal() {
      return this.proyeccion.legal || {
        totalMinimoAPagar: 0,
        totalMinimoCancelarCredito: 0,
        cantidadCreditos: 0,
      };
    },
    total() {
      return this.proyeccion.total || {
        totalMinimoAPagar: 0,
        totalMinimoCancelarCredito: 0,
        cantidadCreditos: 0,
      };
    },
    cards() {
      return [
        {
          key: "activos",
          title: "Activos",
          badgeLabel: "",
          badgeClass: "bg-success-subtle text-success-emphasis",
          cardClass: "estado-activo",
          data: this.activos,
        },
        {
          key: "vencidos",
          title: "Vencidos",
          badgeLabel: "",
          badgeClass: "bg-warning-subtle text-warning-emphasis",
          cardClass: "estado-vencido",
          data: this.vencidos,
        },
        {
          key: "legal",
          title: "Legal",
          badgeLabel: "COBRO LEGAL",
          badgeClass: "bg-danger-subtle text-danger-emphasis",
          cardClass: "estado-legal",
          data: this.legal,
        },
        {
          key: "total",
          title: "Total",
          badgeLabel: "GLOBAL",
          badgeClass: "bg-info-subtle text-info-emphasis",
          cardClass: "estado-total",
          data: this.total,
        },
      ];
    },
    tieneDatos() {
      return this.total.cantidadCreditos > 0;
    },
    fechaProyeccionFormateada() {
      // Si el backend devuelve "0001-01-01..." usamos la fecha seleccionada
      if (
        !this.proyeccion.fechaProyeccion ||
        this.proyeccion.fechaProyeccion.startsWith("0001-01-01")
      ) {
        // Construimos un ISO simple con la fecha seleccionada
        return this.dateFormat(this.selectedDate + "T00:00:00");
      }

      return this.dateFormat(this.proyeccion.fechaProyeccion);
    },
  },
  beforeMount() {
    this.getProyeccion();
  },
  methods: {
    getProyeccion() {
      this.ready = false;
      this.dateError = "";

      // Validación de fecha (no permitir menor a hoy)
      if (!this.selectedDate) {
        this.dateError = "Seleccione una fecha de proyección.";
        this.ready = true;
        return;
      }

      if (this.selectedDate < this.today) {
        this.dateError = "La fecha debe ser hoy o una fecha futura.";
        this.selectedDate = this.today;
        this.ready = true;
        return;
      }

      const fecha = new Date(this.selectedDate + "T00:00:00");

      const payload = {
        fechaProyeccion: fecha, // Se enviará como ISO al hacer JSON.stringify
        sucursales: ["ALL"],
      };

      fetch(
        process.env.VUE_APP_API_URL + `/admin/credito/proyeccion`,
        Security.requestOptions(payload)
      )
        .then((response) => response.json())
        .then((response) => {
          if (response.error) {
            Swal.fire({
              position: "top-end",
              icon: "error",
              title: response.error,
              showConfirmButton: false,
              timer: 1500,
            });
          } else {
            // Aquí va el JSON que mostraste en el mensaje
            // {
            //   "activos": {...},
            //   "vencidos": {...},
            //   "legal": {...},
            //   "total": {...},
            //   "fechaProyeccion": "0001-01-01T00:00:00Z"
            // }
            this.proyeccion = response.data || this.proyeccion;

            this.ready = true;
            console.log("Proyección:", response);
          }
        })
        .catch((error) => {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: error,
            showConfirmButton: false,
            timer: 1500,
          });
          this.ready = true;
        });
    },

    handleConsultar() {
      this.getProyeccion();
    },

    getPorcentajeCreditos(cantidad) {
      const totalCreditos = this.total.cantidadCreditos || 0;
      if (!totalCreditos) return 0;
      return Math.round((cantidad / totalCreditos) * 100);
    },

    formatoNumero(numero) {
      return new Intl.NumberFormat("en-US").format(numero || 0);
    },
    formatoNumero2(numero) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numero || 0);
    },

    dateFormat(param) {
      const fecha = new Date(param);

      const dia = ("0" + fecha.getUTCDate()).slice(-2);
      const mes = ("0" + (fecha.getUTCMonth() + 1)).slice(-2);
      const anio = fecha.getUTCFullYear();

      return `${dia}/${mes}/${anio}`;
    },

    async handleImprimir() {
      if (!this.tieneDatos) {
        Swal.fire({
          position: "top-end",
          icon: "info",
          title: "No hay datos para exportar.",
          showConfirmButton: false,
          timer: 1500,
        });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Proyección");

      // Título
      worksheet.mergeCells("A1:E1");
      worksheet.getCell("A1").value = "Proyección de Cartera";
      worksheet.getCell("A1").font = { bold: true, size: 14 };
      worksheet.getCell("A1").alignment = { horizontal: "center" };

      worksheet.addRow([]);
      const headerRow = worksheet.addRow([
        "Estado",
        "Cantidad Créditos",
        "Total mínimo a pagar (L)",
        "Total para cancelar (L)",
        "Fecha de Proyección",
      ]);
      headerRow.font = { bold: true };

      const fechaTexto = this.fechaProyeccionFormateada;

      const rows = [
        [
          "Activos",
          this.activos.cantidadCreditos,
          this.activos.totalMinimoAPagar,
          this.activos.totalMinimoCancelarCredito,
          fechaTexto,
        ],
        [
          "Vencidos",
          this.vencidos.cantidadCreditos,
          this.vencidos.totalMinimoAPagar,
          this.vencidos.totalMinimoCancelarCredito,
          fechaTexto,
        ],
        [
          "Legal",
          this.legal.cantidadCreditos,
          this.legal.totalMinimoAPagar,
          this.legal.totalMinimoCancelarCredito,
          fechaTexto,
        ],
        [
          "Total",
          this.total.cantidadCreditos,
          this.total.totalMinimoAPagar,
          this.total.totalMinimoCancelarCredito,
          fechaTexto,
        ],
      ];

      worksheet.addRows(rows);

      worksheet.columns = [
        { key: "estado", width: 15 },
        { key: "cantidad", width: 20 },
        { key: "minimo", width: 28 },
        { key: "cancelar", width: 28 },
        { key: "fecha", width: 20 },
      ];

      ["C", "D"].forEach((col) => {
        worksheet.getColumn(col).numFmt = "#,##0.00";
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute(
        "download",
        `Proyeccion-${this.selectedDate || this.today}.xlsx`
      );
      a.href = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
  },
};
</script>

<style scoped>
.proyeccion-container {
  max-width: 1200px;
}

/* Títulos */
.title {
  font-weight: 700;
  font-size: 1.6rem;
}

.subtitle {
  color: #6c757d;
}

/* Tarjetas de estado */
.estado-card {
  border-radius: 1rem;
  padding: 1.2rem 1.2rem 1rem;
  background: #ffffff;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.estado-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 35px rgba(15, 23, 42, 0.16);
}

.estado-card-header {
  font-size: 0.9rem;
  margin-bottom: 0.6rem;
}

.estado-name {
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

/* Colores suaves según estado */
.estado-activo {
  background: radial-gradient(circle at top left, #e0fbea, #ffffff);
}

.estado-vencido {
  background: radial-gradient(circle at top left, #fff7d6, #ffffff);
}

.estado-legal {
  background: radial-gradient(circle at top left, #ffe4e4, #ffffff);
}

.estado-total {
  background: radial-gradient(circle at top left, #dbeafe, #ffffff);
}

.estado-monto-principal {
  font-size: 1.4rem;
  font-weight: 700;
}

.moneda {
  font-size: 0.85rem;
  font-weight: 500;
  color: #6b7280;
  margin-left: 0.2rem;
}

.estado-label {
  font-size: 0.85rem;
  color: #6b7280;
}

.estado-secondary {
  font-size: 0.85rem;
  color: #4b5563;
}

.estado-secondary strong {
  font-size: 0.95rem;
}

.estado-footer {
  font-size: 0.8rem;
  color: #6b7280;
}

.estado-creditos {
  font-weight: 500;
}

/* Barra de proporción de créditos */
.estado-bar {
  flex: 1;
  margin-left: 0.8rem;
  height: 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.35);
  overflow: hidden;
}

.estado-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #059669);
  border-radius: 999px;
  transition: width 0.25s ease;
}

/* Tarjeta total */
.total-card {
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  background: #0f172a;
  color: #e5e7eb;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.6);
}

.total-card h5 {
  font-weight: 600;
}

.total-card p {
  color: #cbd5f5 !important;
}

/* Badges con colores suaves (por si no tienes Bootstrap 5.3) */
.bg-success-subtle {
  background-color: #dcfce7 !important;
}

.text-success-emphasis {
  color: #166534 !important;
}

.bg-warning-subtle {
  background-color: #fef3c7 !important;
}

.text-warning-emphasis {
  color: #92400e !important;
}

.bg-danger-subtle {
  background-color: #fee2e2 !important;
}

.text-danger-emphasis {
  color: #b91c1c !important;
}

.bg-info-subtle {
  background-color: #e0f2fe !important;
}

.text-info-emphasis {
  color: #0369a1 !important;
}
</style>
